/**
 * Tasks API Service - Memory Agent Task Management
 *
 * Connects to the memory-agent Tasks API via orbit-gateway proxy
 * for persistent task management with context preservation.
 *
 * Routes (via orbit-gateway):
 * - POST /api/tasks/create - Create a new task
 * - POST /api/tasks/list - List all tasks for a user
 * - POST /api/tasks/switch - Switch to a different task (preserves context)
 * - POST /api/tasks/active - Get the currently active task
 */
import { getIdToken } from './firebase';

// Use orbit-gateway as proxy to internal services
const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const TASKS_API_BASE = `${ORBIT_GATEWAY_URL}/api/tasks`;

// =============================================================================
// TYPES
// =============================================================================

export interface Task {
  task_id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'pending' | 'running' | 'paused' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high';
  project_id?: string;  // Project association
  tags?: string[];
  due_date?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  has_context?: boolean;
  context_snapshot?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface CreateTaskRequest {
  user_id: string;
  title: string;
  description?: string;
  project_id?: string;  // Associate task with a project
  metadata?: Record<string, unknown>;
  due_date?: string;
  auto_execute?: boolean;
}

export interface ExecuteTaskResponse {
  success: boolean;
  task_id?: string;
  status?: string;
  result?: string;
  error?: string;
}

export interface CreateTaskResponse {
  success: boolean;
  task: Task;
  message?: string;
}

export interface ListTasksRequest {
  user_id: string;
  status?: 'active' | 'paused' | 'completed' | 'archived' | 'all';
  project_id?: string;  // Filter tasks by project
  limit?: number;
}

export interface ListTasksResponse {
  success: boolean;
  tasks: Task[];
  total: number;
}

export interface SwitchTaskRequest {
  user_id: string;
  task_id: string;
  preserve_context?: boolean;
}

export interface SwitchTaskResponse {
  success: boolean;
  previous_task?: Task;
  current_task: Task;
  context_preserved: boolean;
  message?: string;
}

export interface UpdateTaskRequest {
  user_id: string;
  task_id: string;
  title?: string;
  description?: string;
  status?: 'pending' | 'running' | 'paused' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high';
  project_id?: string;
  tags?: string[];
  due_date?: string;
}

export interface UpdateTaskResponse {
  success: boolean;
  task: Task;
  message?: string;
}

export interface ActiveTaskRequest {
  user_id: string;
}

export interface ActiveTaskResponse {
  success: boolean;
  task: Task | null;
  message?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get authenticated headers with Firebase token
 */
async function getHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Make a POST request to the Tasks API via orbit-gateway proxy
 * 
 * @param endpoint - The task endpoint (e.g., 'create', 'list', 'switch')
 * @param body - Request body (user_id is injected by gateway)
 */
async function postTasksApi<T, R extends object = object>(endpoint: string, body: R): Promise<T> {
  const headers = await getHeaders();

  // Remove leading slash if present, gateway expects /api/tasks/create not /api/tasks//tasks/create
  const cleanEndpoint = endpoint.replace(/^\/tasks/, '');
  
  const response = await fetch(`${TASKS_API_BASE}${cleanEndpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Tasks API error: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Create a new task
 */
export async function createTask(request: CreateTaskRequest): Promise<CreateTaskResponse> {
  try {
    const response = await postTasksApi<CreateTaskResponse>('/tasks/create', request);
    console.log('[TasksAPI] Task created:', response.task?.task_id);
    return response;
  } catch (error) {
    console.error('[TasksAPI] Error creating task:', error);
    throw error;
  }
}

/**
 * List tasks for a user
 */
export async function listTasks(request: ListTasksRequest): Promise<ListTasksResponse> {
  try {
    const response = await postTasksApi<ListTasksResponse>('/tasks/list', request);
    console.log('[TasksAPI] Listed tasks:', response.tasks?.length);
    return response;
  } catch (error) {
    console.error('[TasksAPI] Error listing tasks:', error);
    throw error;
  }
}

/**
 * Switch to a different task (preserves context of current task)
 */
export async function switchTask(request: SwitchTaskRequest): Promise<SwitchTaskResponse> {
  try {
    const response = await postTasksApi<SwitchTaskResponse>('/tasks/switch', {
      ...request,
      preserve_context: request.preserve_context ?? true
    });
    console.log('[TasksAPI] Switched to task:', response.current_task?.task_id);
    return response;
  } catch (error) {
    console.error('[TasksAPI] Error switching task:', error);
    throw error;
  }
}

/**
 * Get the currently active task
 */
export async function getActiveTask(request: ActiveTaskRequest): Promise<ActiveTaskResponse> {
  try {
    const response = await postTasksApi<ActiveTaskResponse>('/tasks/active', request);
    console.log('[TasksAPI] Active task:', response.task?.task_id || 'none');
    return response;
  } catch (error) {
    console.error('[TasksAPI] Error getting active task:', error);
    throw error;
  }
}

/**
 * Update a task (status, title, description, priority, project, etc.)
 */
export async function updateTask(request: UpdateTaskRequest): Promise<UpdateTaskResponse> {
  try {
    const { task_id, ...updateData } = request;
    const response = await postTasksApi<UpdateTaskResponse>(`/tasks/${task_id}/update`, updateData);
    console.log('[TasksAPI] Task updated:', task_id);
    return response;
  } catch (error) {
    console.error('[TasksAPI] Error updating task:', error);
    throw error;
  }
}

/**
 * Delete a task
 */
export async function deleteTask(taskId: string, userId: string): Promise<{ success: boolean }> {
  try {
    const response = await postTasksApi<{ success: boolean }>(`/tasks/${taskId}/update`, {
      user_id: userId,
      status: 'cancelled'  // Soft delete via status change
    });
    console.log('[TasksAPI] Task deleted (cancelled):', taskId);
    return response;
  } catch (error) {
    console.error('[TasksAPI] Error deleting task:', error);
    throw error;
  }
}

/**
 * Execute a task — sends it to the AI pipeline as an active agent
 */
export async function executeTask(taskId: string, userId: string): Promise<ExecuteTaskResponse> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${TASKS_API_BASE}/${taskId}/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userId })
    });
    const result = await response.json();
    console.log('[TasksAPI] Task executed:', taskId, result.success);
    return result;
  } catch (error) {
    console.error('[TasksAPI] Error executing task:', error);
    return { success: false, error: String(error) };
  }
}
